import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { 
  Plus, MagnifyingGlass, FileText, Download, Trash,
  Eye, CurrencyEur, Calendar, EnvelopeSimple, PaperPlaneTilt, CircleNotch
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import YearMonthNav from '../components/YearMonthNav';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

import { API } from '../config/api';

const statusLabelsMap = {
  it: { draft: 'Bozza', sent: 'Inviato', accepted: 'Accettato', rejected: 'Rifiutato', won: 'Vinto', lost: 'Perso' },
  es: { draft: 'Borrador', sent: 'Enviado', accepted: 'Aceptado', rejected: 'Rechazado', won: 'Ganado', lost: 'Perdido' }
};

const statusColors = {
  draft: 'bg-gray-100 text-gray-800',
  sent: 'bg-blue-100 text-blue-800',
  accepted: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  won: 'bg-green-100 text-green-800',
  lost: 'bg-red-100 text-red-800'
};

export default function QuotesPage() {
  const { t, i18n } = useTranslation();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(null);
  const navigate = useNavigate();

  const statusLabels = statusLabelsMap[i18n.language] || statusLabelsMap.it;

  // Email dialog
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailQuote, setEmailQuote] = useState(null);
  const [emailForm, setEmailForm] = useState({ recipient_email: '', subject: '', message: '' });
  const [sendingEmail, setSendingEmail] = useState(false);

  const fetchQuotes = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      
      const { data } = await axios.get(`${API}/quotes?${params}`);
      setQuotes(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error('Error fetching quotes:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes();
  }, [search, statusFilter]);

  const handleDelete = async (quoteId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo preventivo?')) return;
    try {
      await axios.delete(`${API}/quotes/${quoteId}`);
      fetchQuotes();
    } catch (err) {
      console.error('Error deleting quote:', err);
    }
  };

  const handleDownloadPdf = async (quoteId, quoteNumber) => {
    try {
      const response = await axios.get(`${API}/quotes/${quoteId}/pdf`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `preventivo_${quoteNumber}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
    }
  };

  const openEmailDialog = async (quote) => {
    setEmailQuote(quote);
    // Try to get client email
    let clientEmail = '';
    if (quote.client_id) {
      try {
        const { data } = await axios.get(`${API}/clients/${quote.client_id}`);
        clientEmail = data.email || '';
      } catch (err) { /* ignore */ }
    }
    setEmailForm({
      recipient_email: clientEmail,
      subject: `Preventivo #${quote.quote_number}`,
      message: ''
    });
    setEmailDialogOpen(true);
  };

  const sendQuoteEmail = async () => {
    if (!emailForm.recipient_email) { toast.error(t('emailDialog.error')); return; }
    setSendingEmail(true);
    try {
      const { data } = await axios.post(`${API}/email/send-quote`, {
        quote_id: emailQuote.id,
        recipient_email: emailForm.recipient_email,
        subject: emailForm.subject || undefined,
        message: emailForm.message || undefined
      });
      toast.success(data.message || t('emailDialog.sent'));
      setEmailDialogOpen(false);
      fetchQuotes(); // the quote is now "Inviato" and its automatic reminders are scheduled
    } catch (err) {
      toast.error(err.response?.data?.detail || t('emailDialog.error'));
    } finally { setSendingEmail(false); }
  };

  // CSV Export
  const exportQuotesCsv = () => {
    const headers = ['quote_number', 'client_name', 'status', 'total_amount', 'items_count', 'created_at'];
    const csvRows = [headers.join(',')];
    (quotes || []).forEach(q => {
      csvRows.push([
        `"${q.quote_number || ''}"`,
        `"${(q.client_name || '').replace(/"/g, '""')}"`,
        `"${q.status || ''}"`,
        q.total_amount || 0,
        q.items?.length || 0,
        `"${q.created_at || ''}"`,
      ].join(','));
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'preventivi.csv'; a.click();
    URL.revokeObjectURL(url);
    toast.success(t('quotes.exportSuccess'));
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const locale = i18n.language === 'es' ? 'es-ES' : 'it-IT';
    return date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const filteredQuotes = (quotes || []).filter(q => {
    const raw = q.created_at;
    if (!raw) return false;
    const d = new Date(raw);
    if (d.getFullYear() !== selectedYear) return false;
    if (selectedMonth !== null && d.getMonth() !== selectedMonth) return false;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="quotes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#0A0F1A]">
            {t('quotes.title')}
          </h1>
          <p className="text-[#4A5568] mt-1">
            {t('quotes.subtitle')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportQuotesCsv} className="border-[#0033A0] text-[#0033A0]" data-testid="export-quotes-btn">
            <Download size={18} className="mr-1" /> {t('common.export')} CSV
          </Button>
          <Button
            onClick={() => navigate('/quotes/new')}
            className="bg-[#0033A0] hover:bg-[#0047AB] text-white"
            data-testid="new-quote-button"
          >
            <Plus size={20} className="mr-2" />
            {t('quotes.newQuote')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" size={20} />
          <Input
            placeholder={t('quotes.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 border-[#E2E8F0]"
            data-testid="quote-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48 border-[#E2E8F0]" data-testid="quote-status-filter">
            <SelectValue placeholder="Tutti gli stati" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('quotes.allStatuses')}</SelectItem>
            {Object.entries(statusLabels).map(([value, label]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <YearMonthNav
        items={quotes}
        dateField="created_at"
        totalField="total"
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        selectedMonth={selectedMonth}
        setSelectedMonth={setSelectedMonth}
      />

      {/* Quotes List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0033A0]"></div>
        </div>
      ) : filteredQuotes.length === 0 ? (
        <div className="text-center py-12 bg-[#F4F6F8] rounded-sm border border-[#E2E8F0]">
          <FileText size={48} className="mx-auto text-[#4A5568] mb-4" />
          <h3 className="font-medium text-[#0A0F1A] mb-2">{t('quotes.noQuotes')}</h3>
          <p className="text-sm text-[#4A5568] mb-4">
            {i18n.language === 'es' ? 'Empieza creando tu primer presupuesto' : 'Inizia creando il tuo primo preventivo'}
          </p>
          <Button
            onClick={() => navigate('/quotes/new')}
            className="bg-[#0033A0] hover:bg-[#0047AB] text-white"
          >
            <Plus size={20} className="mr-2" />
            {t('quotes.newQuote')}
          </Button>
        </div>
      ) : (
        <div className="border border-[#E2E8F0] rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F4F6F8]">
                <tr>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-bold text-[#4A5568]">
                    {t('quotes.quoteNumber')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-bold text-[#4A5568]">
                    {t('quotes.client')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-bold text-[#4A5568] hidden md:table-cell">
                    {t('quotes.date')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs uppercase tracking-wider font-bold text-[#4A5568]">
                    {t('quotes.total')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-bold text-[#4A5568]">
                    {t('quotes.status')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs uppercase tracking-wider font-bold text-[#4A5568]">
                    {t('common.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#E2E8F0]">
                {filteredQuotes.map((quote) => (
                  <tr key={quote.id} className="hover:bg-[#F4F6F8] transition-colors" data-testid={`quote-row-${quote.id}`}>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-sm bg-[#0033A0]/10 flex items-center justify-center">
                          <FileText size={20} className="text-[#0033A0]" />
                        </div>
                        <div>
                          <p className="font-medium text-[#0A0F1A]">{quote.quote_number}</p>
                          <p className="text-xs text-[#4A5568]">{quote.items?.length || 0} prodotti</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm text-[#0A0F1A]">{quote.client_name}</p>
                    </td>
                    <td className="px-4 py-4 hidden md:table-cell">
                      <div className="flex items-center gap-2 text-sm text-[#4A5568]">
                        <Calendar size={14} />
                        {formatDate(quote.created_at)}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <p className="font-bold text-[#0033A0] flex items-center justify-end gap-1">
                        <CurrencyEur size={16} />
                        {(quote.total || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                      </p>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`px-2 py-1 rounded-sm text-xs font-medium ${statusColors[quote.status]}`}>
                        {statusLabels[quote.status]}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/quotes/${quote.id}`)}
                          className="text-[#0033A0] hover:bg-[#0033A0]/10"
                          data-testid={`view-quote-${quote.id}`}
                        >
                          <Eye size={18} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownloadPdf(quote.id, quote.quote_number)}
                          className="text-[#4A5568] hover:text-[#0A0F1A]"
                          data-testid={`download-quote-${quote.id}`}
                          title="Scarica PDF"
                        >
                          <Download size={18} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEmailDialog(quote)}
                          className="text-[#0033A0] hover:bg-[#0033A0]/10"
                          data-testid={`email-quote-${quote.id}`}
                          title="Invia via Email"
                        >
                          <EnvelopeSimple size={18} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(quote.id)}
                          className="text-red-600 hover:bg-red-50"
                          data-testid={`delete-quote-${quote.id}`}
                        >
                          <Trash size={18} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Email Quote Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl flex items-center gap-2">
              <EnvelopeSimple size={22} className="text-[#0033A0]" />
              {t('emailDialog.title')}
            </DialogTitle>
          </DialogHeader>
          {emailQuote && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-[#F4F6F8] rounded-sm text-sm">
                <p className="font-medium text-[#0A0F1A]">Preventivo #{emailQuote.quote_number}</p>
                <p className="text-[#4A5568]">{emailQuote.client_name} &mdash; &euro;{emailQuote.total_amount?.toFixed(2)}</p>
              </div>
              <div>
                <Label>{t('emailDialog.recipient')}</Label>
                <Input
                  type="email"
                  value={emailForm.recipient_email}
                  onChange={(e) => setEmailForm({ ...emailForm, recipient_email: e.target.value })}
                  placeholder="email@cliente.it"
                  className="mt-1"
                  data-testid="email-recipient-input"
                />
              </div>
              <div>
                <Label>{t('emailDialog.subject')}</Label>
                <Input
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                  placeholder="Preventivo"
                  className="mt-1"
                  data-testid="email-subject-input"
                />
              </div>
              <div>
                <Label>{t('emailDialog.message')} <span className="text-xs text-[#4A5568]">({t('emailDialog.messageOptional')})</span></Label>
                <Textarea
                  value={emailForm.message}
                  onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                  placeholder="Es. In allegato trova il preventivo richiesto..."
                  rows={3}
                  className="mt-1"
                  data-testid="email-message-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>Annulla</Button>
                <Button
                  onClick={sendQuoteEmail}
                  disabled={sendingEmail || !emailForm.recipient_email}
                  className="bg-[#0033A0] hover:bg-[#0047AB] text-white"
                  data-testid="send-email-btn"
                >
                  {sendingEmail ? <CircleNotch size={16} className="animate-spin mr-1" /> : <PaperPlaneTilt size={16} className="mr-1" />}
                  {t('emailDialog.sendWithPdf')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

